import {
  Box,
  InputAdornment,
  IconButton,
  Typography,
  Fade,
} from "@mui/material";
import { Copy, Eye } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import TextField from "components/common/TextField";

export default ({
  value,
  label,
  onReveal,
  onCopy,
  sx,
  multiline = false,
}: {
  value?: string | null;
  label?: string | null;
  onReveal?: () => Promise<string>;
  onCopy?: () => void;
  sx?: React.CSSProperties;
  multiline?: boolean;
}) => {
  const [revealedValue, setRevealeValue] = useState<string | null>(
    value || null,
  );
  const [copied, setCopied] = useState(false);
  const textFieldRef = useRef<HTMLInputElement>(null); // Ref to access the input

  // Update revealedValue when value prop changes
  useEffect(() => {
    if (!onReveal) {
      setRevealeValue(value || null);
    }
  }, [value, onReveal]);

  const handleCopy = async (selectText: boolean) => {
    if (revealedValue) {
      if (selectText && textFieldRef.current) {
        textFieldRef.current.select();
      }
      await navigator.clipboard.writeText(revealedValue);
      setCopied(true);
      onCopy?.();
      setTimeout(() => setCopied(false), 1500); // Hide "Copied" after 1.5 seconds
    }
  };

  const handleReveal = async () => {
    const revealed = await onReveal?.();
    setRevealeValue(revealed || null);
  };

  return (
    <TextField
      fullWidth
      variant="outlined"
      value={revealedValue || (onReveal ? "**********" : "")}
      inputRef={textFieldRef}
      label={label}
      multiline={multiline}
      InputProps={{
        readOnly: true,
        endAdornment:
          revealedValue || !onReveal ? (
            <InputAdornment
              position="end"
              sx={{
                paddingTop: "0px !important",
                paddingLeft: "15px !important",
                paddingRight: "12px",
                height: "unset !important",
                alignSelf: "start",
                marginTop: "18px",
              }}
            >
              <Box
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Fade in={!copied} timeout={300}>
                  <IconButton
                    onClick={(e) => {
                      // Otherwise the click bubbles to the TextField and copies twice.
                      e.stopPropagation();
                      handleCopy(false);
                    }}
                    edge="end"
                  >
                    <Copy width="20" />
                  </IconButton>
                </Fade>
                <Fade in={copied} timeout={300}>
                  <Typography
                    variant="body2"
                    color="primary"
                    sx={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontWeight: "700",
                      paddingRight: "10px",
                    }}
                  >
                    Copied
                  </Typography>
                </Fade>
              </Box>
            </InputAdornment>
          ) : (
            <InputAdornment
              position="end"
              sx={{
                paddingTop: "0px !important",
                paddingLeft: "15px !important",
                paddingRight: "12px",
              }}
            >
              <Box
                position="relative"
                display="flex"
                alignItems="center"
                justifyContent="center"
              >
                <Fade in={!revealedValue} timeout={300}>
                  <IconButton
                    onClick={handleReveal}
                    edge="end"
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    <Eye width="20" />
                  </IconButton>
                </Fade>
              </Box>
            </InputAdornment>
          ),
      }}
      onClick={() => handleCopy(true)}
      sx={{
        bgcolor: "#FAF9FA",
        borderRadius: "12px",
        // "& fieldset": { border: "none" },
        ".MuiInputBase-root": {
          borderRadius: "12px",
          cursor: "pointer",
        },
        ".MuiInputBase-root textarea": {
          cursor: "pointer",
          margin: "16px 0 !important",
        },
        ".MuiInputBase-root input": {
          cursor: "pointer",
          marginTop: !label ? "0 !important" : undefined,
          padding: "0 0 0 20px",
        },
        cursor: "pointer",
        ...sx,
      }}
    />
  );
};
